/**
 * t537u: a manager adds time off for someone in their scope (holiday, sickness, any leave type). It's approved
 * straight away, the person is told and it's audited. Before saving, the dialog shows what it will do: hours, the
 * allowance warning, leave already booked on those days, and their shifts, with a choice of leaving them, opening them
 * up for others or removing them. Used from the Time off page, the person's Time off tab and the rota.
 */
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import type { BookablePerson, LeaveBookingInput, LeaveShiftHandling, LeaveType } from '@tradeoutlet/shared';
import { useBookablePeople, useBookLeave, useLeavePreview, useLeaveTypes } from '@tradeoutlet/data';
import { errorMessage } from '@/lib/api';
import { hours } from '@/lib/format';
import { useWorkspaceId } from '@/lib/hooks';
import { useUi } from '@/lib/store';
import { DialogFooter, Field, Input, Modal, Select, Textarea, Toggle } from '@/components/ui';

export interface AddTimeOffTarget {
  userId?: string | null;
  /** Shown before the people list loads (e.g. from the rota row). */
  name?: string | null;
  date?: string | null;
  /** 'sick' pre-selects the sickness type ("Record sickness" / "Mark as off sick"). */
  kind?: 'any' | 'sick';
}

const isSick = (t: Pick<LeaveType, 'code' | 'name'>) => t.code === 'sick' || /\bsick|illness/i.test(t.name);
/** The workspace's main holiday type: Annual leave, else the first type that uses the allowance. */
export function defaultLeaveType(types: LeaveType[], kind: 'any' | 'sick' = 'any'): LeaveType | undefined {
  if (kind === 'sick') { const s = types.find(isSick); if (s) return s; }
  return types.find(t => t.code === 'annual') ?? types.find(t => t.deducts_entitlement) ?? types[0];
}
const fmtDay = (d: string) => DateTime.fromISO(d).setLocale('en-GB').toFormat('ccc d LLL');
export const dayRange = (a: string, b: string) => (a === b ? fmtDay(a) : `${fmtDay(a)} – ${fmtDay(b)}`);
const first = (n?: string | null) => (n ?? '').split(' ')[0] || 'them';
const time = (iso: string) => DateTime.fromISO(iso).setLocale('en-GB').toFormat('HH:mm');
const whatLabel = (t: LeaveType | undefined) => (!t ? 'time off' : isSick(t) ? 'sickness' : t.code === 'annual' || t.deducts_entitlement ? 'holiday' : t.name.toLowerCase());

/** Searchable person combobox limited to the people I can book for. Plain list, no chips. */
function PersonField({ people, value, onChange, fallbackName, loading }: {
  people: BookablePerson[]; value: string; onChange: (id: string) => void; fallbackName?: string | null; loading: boolean;
}) {
  const id = useId();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const chosen = people.find(p => p.user_id === value);
  const term = q.trim().toLowerCase();
  const rows = useMemo(() => (term ? people.filter(p => p.display_name.toLowerCase().includes(term) || (p.location_name ?? '').toLowerCase().includes(term)) : people).slice(0, 50), [people, term]);
  useEffect(() => { setActive(0); }, [term]);
  const pick = (p: BookablePerson) => { onChange(p.user_id); setQ(''); setOpen(false); };
  const key = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive(a => Math.min(rows.length - 1, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(0, a - 1)); }
    else if (e.key === 'Enter' && open && rows[active]) { e.preventDefault(); pick(rows[active]); }
    else if (e.key === 'Escape' && open) { e.preventDefault(); e.stopPropagation(); setOpen(false); }
  };
  const shown = open ? q : chosen?.display_name ?? (value ? fallbackName ?? '' : '');
  return (
    <div className="relative">
      <Input ref={input} role="combobox" aria-expanded={open} aria-controls={`${id}-list`} aria-autocomplete="list" aria-label="Employee" data-testid="add-timeoff-person"
        placeholder={loading ? 'Loading people…' : 'Search by name'} value={shown}
        onFocus={() => { setQ(''); if (!value) setOpen(true); }} onClick={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={e => { setQ(e.target.value); setOpen(true); }} onKeyDown={key} />
      {chosen?.location_name && !open && <p className="mt-1 text-xs text-slate-500">{chosen.location_name}</p>}
      {open && (
        <ul id={`${id}-list`} role="listbox" className="absolute left-0 right-0 z-20 mt-1 max-h-60 overflow-y-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700" data-testid="add-timeoff-people">
          {!rows.length && <li className="px-3 py-2 text-slate-500">{loading ? 'Loading…' : term ? 'No one in your teams matches' : 'No one to book for'}</li>}
          {rows.map((p, i) => (
            <li key={p.user_id} role="option" aria-selected={p.user_id === value} onMouseDown={e => { e.preventDefault(); pick(p); }} onMouseEnter={() => setActive(i)}
              className={clsx('flex cursor-pointer items-baseline gap-2 px-3 py-1.5', i === active && 'bg-slate-100 dark:bg-slate-700')}>
              <span className="truncate">{p.display_name}</span>
              {p.location_name && <span className="ml-auto shrink-0 text-xs text-slate-500">{p.location_name}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const SHIFT_CHOICES: { id: LeaveShiftHandling; label: string; hint: string }[] = [
  { id: 'open', label: 'Open them up', hint: 'Take them off the shift so someone else can pick it up' },
  { id: 'remove', label: 'Remove them', hint: 'Take the shift off the rota' },
  { id: 'keep', label: 'Leave them on the rota', hint: 'Sort the rota out later' },
];

export function AddTimeOffDialog({ open, onClose, onSaved, target }: { open: boolean; onClose: () => void; onSaved?: () => void; target?: AddTimeOffTarget | null }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const people = useBookablePeople(ws, open);
  const types = useLeaveTypes(ws, open);
  const today = DateTime.now().toISODate()!;
  const blank = () => ({ user_id: target?.userId ?? '', leave_type_id: '', start_date: target?.date ?? '', end_date: target?.date ?? '', start_half_day: false, end_half_day: false, reason: '' });
  const [f, setF] = useState(blank);
  const [shifts, setShifts] = useState<LeaveShiftHandling | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Fresh form each time it opens (pre-filled from where it was opened).
  useEffect(() => { if (open) { setF(blank()); setShifts(null); setError(null); } }, [open, target?.userId, target?.date, target?.kind]); // eslint-disable-line react-hooks/exhaustive-deps
  const list = types.data?.leave_types ?? [];
  const typeId = f.leave_type_id || defaultLeaveType(list, target?.kind)?.id || '';
  const type = list.find(t => t.id === typeId);
  const end = f.end_date || f.start_date;
  const input: LeaveBookingInput | null = f.user_id && typeId && f.start_date && end >= f.start_date
    ? { user_id: f.user_id, leave_type_id: typeId, start_date: f.start_date, end_date: end, start_half_day: f.start_half_day, end_half_day: f.end_half_day } : null;
  const preview = useLeavePreview(ws, open ? input : null);
  const p = preview.data;
  const editable = (p?.shifts ?? []).filter(s => s.can_edit);
  // Sickness defaults to opening the shift up for cover; holiday leaves the rota as it is.
  const handling: LeaveShiftHandling = shifts ?? (type && isSick(type) ? 'open' : 'keep');
  const save = useBookLeave(ws);
  const who = people.data?.people.find(x => x.user_id === f.user_id)?.display_name ?? p?.display_name ?? target?.name ?? null;
  const title = target?.kind === 'sick' ? 'Record sickness' : 'Add time off';
  const submit = () => {
    if (!input) return;
    setError(null);
    save.mutate({ ...input, reason: f.reason.trim() || undefined, shifts: editable.length ? handling : 'keep' }, {
      onSuccess: r => {
        const opened = (r.conflicting_shifts ?? []).filter(s => s.outcome === 'opened').length;
        const removed = (r.conflicting_shifts ?? []).filter(s => s.outcome === 'removed').length;
        const left = (r.conflicting_shifts ?? []).filter(s => s.outcome === 'kept' || s.outcome === 'failed').length;
        const extra = [opened && `${opened} shift${opened > 1 ? 's' : ''} opened up`, removed && `${removed} shift${removed > 1 ? 's' : ''} removed`,
          left && `${left} shift${left > 1 ? 's' : ''} still on the rota`].filter(Boolean).join(', ');
        toast(`${type && isSick(type) ? 'Recorded sickness' : `Booked ${whatLabel(type)}`} for ${first(who)}: ${dayRange(input.start_date, input.end_date)}${extra ? ` · ${extra}` : ''}${r.warning ? `. ${r.warning}` : ''}`, r.warning || left ? 'info' : 'success');
        onClose();
        onSaved?.();
      },
      onError: e => setError(errorMessage(e)),
    });
  };
  const blocked = p?.blocked ?? null;
  const clash = (p?.existing ?? []).length > 0;
  return (
    <Modal open={open} onClose={onClose} title={title}
      footer={<DialogFooter onCancel={onClose} onSave={submit} saving={save.isPending} disabled={!input || Boolean(blocked) || clash || preview.isFetching && !p} error={error} saveTestId="add-timeoff-save" />}>
      <div className="space-y-4" data-testid="add-timeoff">
        <Field label="Employee">
          <PersonField people={people.data?.people ?? []} value={f.user_id} onChange={id => setF({ ...f, user_id: id })} fallbackName={target?.name} loading={people.isPending} />
        </Field>
        <Field label="Type">
          <Select value={typeId} data-testid="add-timeoff-type" onChange={e => { setF({ ...f, leave_type_id: e.target.value }); setShifts(null); }}>
            {list.map(t => <option key={t.id} value={t.id}>{t.name}{t.deducts_entitlement ? '' : ' (doesn’t use allowance)'}</option>)}
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="First day"><Input type="date" value={f.start_date} data-testid="add-timeoff-start" onChange={e => setF({ ...f, start_date: e.target.value, end_date: f.end_date && f.end_date >= e.target.value ? f.end_date : e.target.value })} /></Field>
          <Field label="Last day"><Input type="date" min={f.start_date} value={f.end_date} data-testid="add-timeoff-end" onChange={e => setF({ ...f, end_date: e.target.value })} /></Field>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Toggle checked={f.start_half_day} onChange={v => setF({ ...f, start_half_day: v })} label="Half day at start" />
          {end !== f.start_date && <Toggle checked={f.end_half_day} onChange={v => setF({ ...f, end_half_day: v })} label="Half day at end" />}
        </div>
        <Field label="Note (optional)" hint={who ? `${first(who)} will see this` : undefined}>
          <Textarea rows={2} value={f.reason} maxLength={1000} onChange={e => setF({ ...f, reason: e.target.value })} />
        </Field>

        {input && (
          <div className="space-y-2 border-t border-slate-200 pt-3 text-sm dark:border-slate-700" data-testid="add-timeoff-summary" aria-live="polite">
            {preview.isError && <p className="text-rose-700 dark:text-rose-300">{errorMessage(preview.error)}</p>}
            {p && <p>{hours(p.hours)}{type?.deducts_entitlement ? ' from their holiday allowance' : ''} · approved straight away. {first(who)} will be told.</p>}
            {p?.warning && <p className="text-amber-800 dark:text-amber-200" data-testid="add-timeoff-warning">{p.warning}</p>}
            {blocked && <p className="text-rose-700 dark:text-rose-300" data-testid="add-timeoff-blocked">{blocked}</p>}
            {clash && <p className="text-rose-700 dark:text-rose-300" data-testid="add-timeoff-clash">
              {first(who)} already has {p!.existing.map(x => `${x.leave_type_name.toLowerCase()} (${x.status === 'pending' ? 'waiting for approval' : 'booked'}) ${dayRange(x.start_date, x.end_date)}`).join('; ')}. {p!.existing.some(x => x.status === 'pending') ? 'Approve or decline that request first.' : 'Change the dates.'}
            </p>}
            {p && p.shifts.length > 0 && (
              <div data-testid="add-timeoff-shifts">
                <p className="font-medium">{p.shifts.length === 1 ? 'They have a shift then' : `They have ${p.shifts.length} shifts then`}</p>
                <ul className="mt-1 space-y-0.5 text-slate-600 dark:text-slate-300">
                  {p.shifts.map(s => <li key={s.id}>{fmtDay(s.local_date)}, {time(s.starts_at)}–{time(s.ends_at)} · {s.location_name}{s.local_date === today ? ' (today)' : ''}{!s.can_edit ? ' · you can’t change shifts here' : ''}</li>)}
                </ul>
                {editable.length > 0 && (
                  <fieldset className="mt-2 space-y-1" role="radiogroup" aria-label="Their shifts">
                    {SHIFT_CHOICES.map(c => (
                      <label key={c.id} className="flex cursor-pointer items-start gap-2">
                        <input type="radio" name="add-timeoff-shifts" className="mt-1" checked={handling === c.id} onChange={() => setShifts(c.id)} data-testid={`add-timeoff-shifts-${c.id}`} />
                        <span>{c.label} <span className="text-slate-500">· {c.hint}</span></span>
                      </label>
                    ))}
                  </fieldset>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
